// The share-link page's actions for a web app or a presentation: one round
// button, bottom right, whose menu holds "Start a new chat from here" and
// "Download PDF". Two wide buttons there covered the deck's slide arrows and
// the page's own controls. On a deck the button sits above the deck's bar.
import React, { useId, useState } from 'react'
import { Fab, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { printWebappFrame } from '../../utils/printWebappFrame'

/** The deck's bar height (--pt-deck-bar-h in webviz/story.css) plus the gap. */
export const DECK_BAR_CLEARANCE = 56 + 16

export interface PageActionsFabProps {
  frameRef: React.RefObject<HTMLIFrameElement | null>
  onStartChat: () => void
  /** Why the viewer cannot start a chat from here. */
  startChatReason?: string
  /** AI access is still loading, so Start a new chat waits, disabled. */
  startChatPending?: boolean
  /** The page is a deck: keep clear of its bar. */
  isDeck?: boolean
}

export default function PageActionsFab({ frameRef, onStartChat, startChatReason, startChatPending, isDeck }: PageActionsFabProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const reasonId = useId()
  const handleClose = () => setAnchorEl(null)
  const startDisabled = !!startChatReason || !!startChatPending
  const handleStart = () => {
    if (startDisabled) return
    handleClose()
    onStartChat()
  }
  const handlePdf = () => {
    handleClose()
    printWebappFrame(frameRef.current)
  }
  return (
    <>
      <Fab
        color="info"
        size="medium"
        aria-label="Page actions"
        aria-haspopup="menu"
        aria-expanded={anchorEl ? 'true' : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        data-testid="page-actions-fab"
        sx={{ position: 'absolute', right: 16, bottom: isDeck ? DECK_BAR_CLEARANCE : 16 }}
      >
        <MoreHorizIcon />
      </Fab>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        // A disabled action keeps its place in the arrow-key order, so a
        // keyboard or screen-reader user reaches its reason.
        MenuListProps={{ disabledItemsFocusable: true }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mb: 1 } } }}
      >
        <MenuItem
          onClick={handleStart}
          disabled={startDisabled}
          data-testid="dashboard-start-chat"
          // The reason is the item's description, not part of its name.
          aria-label="Start a new chat from here"
          aria-describedby={startChatReason ? reasonId : undefined}
          sx={startChatReason ? { alignItems: 'flex-start', '&.Mui-disabled': { opacity: 1 } } : { color: 'info.main' }}
        >
          <ListItemIcon sx={startDisabled ? { color: 'text.disabled', mt: startChatReason ? 0.5 : 0 } : { color: 'info.main' }}>
            <AddCommentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {startChatReason ? (
            <ListItemText
              primary="Start a new chat from here"
              secondary={startChatReason}
              primaryTypographyProps={{ color: 'text.disabled' }}
              secondaryTypographyProps={{ id: reasonId, color: 'text.secondary', sx: { whiteSpace: 'normal', maxWidth: 280 } }}
            />
          ) : (
            <ListItemText>Start a new chat from here</ListItemText>
          )}
        </MenuItem>
        <MenuItem onClick={handlePdf} data-testid="webapp-download-pdf" sx={{ color: 'info.main' }}>
          <ListItemIcon sx={{ color: 'info.main' }}>
            <PictureAsPdfOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}
